const API_URL = process.env.REACT_APP_API_URL || 'https://api.playkokki.com/';

// Ensure API_URL ends with a slash
const BASE_URL = API_URL.endsWith('/') ? API_URL : `${API_URL}/`;

export interface AnalyticsStats {
  totalVisitors: number;
  totalPageViews: number;
  totalSessions: number;
  avgSessionDuration: number;
  newVisitors: number;
  returningVisitors: number;
  totalEvents: number;
  avgPagesPerSession: number;
}

export interface PageViewData {
  path: string;
  views: number;
  uniqueVisitors: number;
  [key: string]: string | number;
}

export interface ReferrerData {
  source: string;
  sessions: number;
  [key: string]: string | number;
}

export interface DeviceData {
  device: string;
  count: number;
  [key: string]: string | number;
}

export interface BrowserData {
  browser: string;
  count: number;
  [key: string]: string | number;
}

export interface DailyStatsData {
  date: string;
  sessions: number;
  pageViews: number;
  avgDuration: number;
  [key: string]: string | number;
}

export interface ActiveSessionsData {
  activeSessions: number;
}

export interface RecentEvent {
  eventName: string;
  eventCategory: string;
  page: string;
  timestamp: string;
  eventData?: any;
}

export const fetchAnalyticsStats = async (startDate: Date, endDate: Date): Promise<AnalyticsStats> => {
  const response = await fetch(
    `${BASE_URL}analytics/overview?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`
  );
  if (!response.ok) throw new Error('Failed to fetch analytics stats');
  return response.json();
};

export const fetchPageViews = async (startDate: Date, endDate: Date): Promise<PageViewData[]> => {
  const response = await fetch(
    `${BASE_URL}analytics/top-pages?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`
  );
  if (!response.ok) throw new Error('Failed to fetch page views');
  return response.json();
};

export const fetchReferrers = async (startDate: Date, endDate: Date): Promise<ReferrerData[]> => {
  const response = await fetch(
    `${BASE_URL}analytics/traffic-sources?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`
  );
  if (!response.ok) throw new Error('Failed to fetch referrers');
  return response.json();
};

export const fetchDevices = async (startDate: Date, endDate: Date): Promise<DeviceData[]> => {
  const response = await fetch(
    `${BASE_URL}analytics/devices?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`
  );
  if (!response.ok) throw new Error('Failed to fetch devices');
  return response.json();
};

export const fetchBrowsers = async (startDate: Date, endDate: Date): Promise<BrowserData[]> => {
  const response = await fetch(
    `${BASE_URL}analytics/browsers?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`
  );
  if (!response.ok) throw new Error('Failed to fetch browsers');
  return response.json();
};

export const fetchDailyStats = async (startDate: Date, endDate: Date): Promise<DailyStatsData[]> => {
  const response = await fetch(
    `${BASE_URL}analytics/daily?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`
  );
  if (!response.ok) throw new Error('Failed to fetch daily stats');
  return response.json();
};

export const fetchActiveSessions = async (): Promise<ActiveSessionsData> => {
  const response = await fetch(`${BASE_URL}analytics/active`);
  if (!response.ok) throw new Error('Failed to fetch active sessions');
  return response.json();
};

export const fetchRecentEvents = async (limit: number = 20): Promise<RecentEvent[]> => {
  const response = await fetch(`${BASE_URL}analytics/events?limit=${limit}`);
  if (!response.ok) throw new Error('Failed to fetch recent events');
  return response.json();
};

// New API functions for enhanced analytics

export interface GeographicalData {
  country: string;
  count: number;
  [key: string]: string | number;
}

export interface TimezoneData {
  timezone: string;
  count: number;
  [key: string]: string | number;
}

export interface OSData {
  os: string;
  count: number;
  [key: string]: string | number;
}

export interface UTMCampaignData {
  campaign: string;
  source: string;
  medium: string;
  sessions: number;
  [key: string]: string | number;
}

export interface VisitorData {
  visitorId: string;
  firstVisit: string;
  lastVisit: string;
  totalVisits: number;
  isRegistered: boolean;
  userId: number | null;
  deviceType: string | null;
  browser: string | null;
  os: string | null;
  country: string | null;
  city: string | null;
  timezone: string | null;
  language: string | null;
  _count: {
    sessions: number;
    pageViews: number;
    events: number;
  };
}

export interface VisitorListResponse {
  visitors: VisitorData[];
  total: number;
  limit: number;
  offset: number;
}

export const fetchGeographicalData = async (startDate: Date, endDate: Date): Promise<GeographicalData[]> => {
  const response = await fetch(
    `${BASE_URL}analytics/geo?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`
  );
  if (!response.ok) throw new Error('Failed to fetch geographical data');
  return response.json();
};

export const fetchTimezoneData = async (startDate: Date, endDate: Date): Promise<TimezoneData[]> => {
  const response = await fetch(
    `${BASE_URL}analytics/timezones?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`
  );
  if (!response.ok) throw new Error('Failed to fetch timezone data');
  return response.json();
};

export const fetchOSData = async (startDate: Date, endDate: Date): Promise<OSData[]> => {
  const response = await fetch(
    `${BASE_URL}analytics/os?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`
  );
  if (!response.ok) throw new Error('Failed to fetch OS data');
  return response.json();
};

export const fetchUTMCampaigns = async (startDate: Date, endDate: Date): Promise<UTMCampaignData[]> => {
  const response = await fetch(
    `${BASE_URL}analytics/utm-campaigns?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`
  );
  if (!response.ok) throw new Error('Failed to fetch UTM campaigns');
  return response.json();
};

export const fetchVisitors = async (
  startDate: Date,
  endDate: Date,
  limit: number = 50,
  offset: number = 0
): Promise<VisitorListResponse> => {
  const response = await fetch(
    `${BASE_URL}analytics/visitors?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&limit=${limit}&offset=${offset}`
  );
  if (!response.ok) throw new Error('Failed to fetch visitors');
  return response.json();
};

export const fetchVisitorDetails = async (visitorId: string): Promise<any> => {
  const response = await fetch(`${BASE_URL}analytics/visitors/${visitorId}`);
  if (!response.ok) throw new Error('Failed to fetch visitor details');
  return response.json();
};

export const fetchSessionDetails = async (sessionId: string): Promise<any> => {
  const response = await fetch(`${BASE_URL}analytics/sessions/${sessionId}`);
  if (!response.ok) throw new Error('Failed to fetch session details');
  return response.json();
};

export interface UserStats {
  hopsCreated: number;
  totalHopSessions: number;
  sessionsAdministered: number;
  sessionsPlayed: number;
  sessionsCompleted: number;
  wins: number;
  winRate: number;
}

export interface UserWithStats {
  id: number;
  email: string;
  name: string;
  nickname: string;
  createdAt: string;
  isConfirmed: boolean;
  stats: UserStats;
}

export interface UsersResponse {
  users: UserWithStats[];
  total: number;
  limit: number;
  offset: number;
}

export const fetchUsersWithStats = async (limit: number = 50, offset: number = 0, search?: string): Promise<UsersResponse> => {
  const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
  if (search) params.append('search', search);
  const response = await fetch(`${BASE_URL}analytics/users?${params}`);
  if (!response.ok) throw new Error('Failed to fetch users');
  return response.json();
};

export const fetchUserDetailedStats = async (userId: number): Promise<any> => {
  const response = await fetch(`${BASE_URL}analytics/users/${userId}`);
  if (!response.ok) throw new Error('Failed to fetch user details');
  return response.json();
};
